import { Controller } from "@hotwired/stimulus";
import { visit } from "@hotwired/turbo";
import { driver } from "driver.js";

export default class extends Controller {
  static values = {
    track: String,
    tenantName: String,
    autoStart: Boolean,
    dismissUrl: String,
    csrfToken: String,
    dashboardUrl: String,
    streamStatusUrl: String,
    adaptersUrl: String,
    steps: Object,
    navNext: String,
    navPrev: String,
    navDone: String,
  };

  // Set to true while navigating so onDestroyed does not fire the dismiss fetch.
  #navigating = false;
  // Set to true when user clicks Done on the last step.
  #completedTour = false;

  connect() {
    const step = sessionStorage.getItem("sc_tour_step");
    const track = sessionStorage.getItem("sc_tour_track");
    if (step !== null && track === this.trackValue) {
      sessionStorage.removeItem("sc_tour_step");
      sessionStorage.removeItem("sc_tour_track");
      this.#launch(parseInt(step, 10));
    } else if (this.autoStartValue) {
      this.#launch(0);
    }
  }

  start() {
    this.#launch(0);
  }
  restart() {
    this.#launch(0);
  }

  async #launch(fromStep) {
    const live = await this.#fetchStreamLive();
    const steps = this.#prepareSteps(live);
    this.#navigating = false;

    this.tour = driver({
      showProgress: true,
      showButtons: ["next", "previous", "close"],
      nextBtnText: this.navNextValue || "Next →",
      prevBtnText: this.navPrevValue || "← Back",
      doneBtnText: this.navDoneValue || "Done",
      allowClose: true,
      overlayOpacity: 0.55,
      stagePadding: 8,
      stageRadius: 8,
      popoverClass: "sc-tour-popover",
      onDestroyed: () => {
        if (!this.#navigating) this.#dismiss();
        if (this.#completedTour && this.hasAdaptersUrlValue) {
          this.#completedTour = false;
          visit(this.adaptersUrlValue);
        }
      },
      onNextClick: () => this.#handleNext(steps),
      steps,
    });

    this.tour.drive(fromStep);
  }

  // If the next step requires a different page, navigate there via Turbo and
  // store the step index in sessionStorage so connect() can resume on arrival.
  #handleNext(steps) {
    const nextIdx = this.tour.getActiveIndex() + 1;
    const next = steps[nextIdx];

    if (next?.navigate) {
      const target = new URL(next.navigate, window.location.origin).pathname;
      if (window.location.pathname !== target) {
        this.#navigating = true;
        sessionStorage.setItem("sc_tour_track", this.trackValue);
        sessionStorage.setItem("sc_tour_step", String(nextIdx));
        this.tour.destroy();
        visit(next.navigate);
        return;
      }
    }
    if (nextIdx >= steps.length) this.#completedTour = true;
    this.tour.moveNext();
  }

  // Fetch live session status from the server only when needed (owner track).
  // Falls back to false on network error so the tour still works offline.
  async #fetchStreamLive() {
    if (this.trackValue !== "owner" || !this.hasStreamStatusUrlValue) return false;
    try {
      const r = await fetch(this.streamStatusUrlValue, { headers: { Accept: "application/json" } });
      if (!r.ok) return false;
      return (await r.json()).session !== null;
    } catch {
      return false;
    }
  }

  // Strip the element selector from steps whose target doesn't exist on the
  // current page AND have no navigate URL (so they become floating popovers).
  // Steps with navigate are kept intact — the element will exist after navigation.
  #prepareSteps(live) {
    return this.#buildSteps(live).map((step) => {
      if (step.element && !document.querySelector(step.element) && !step.navigate) {
        const { element: _element, ...rest } = step;
        return rest;
      }
      return step;
    });
  }

  #escapeHtml(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // Substitutes {token} placeholders in translated step text with dynamic values.
  // driver.js renders popover titles/descriptions as raw HTML, so every substituted
  // value is escaped here — structurally, not by convention — since vars can carry
  // tenant-owner-settable data (e.g. tenantNameValue). Deliberately {token} not
  // %{token} (Rails I18n syntax) so the server can ship these strings without
  // attempting its own interpolation.
  #fill(str, vars) {
    return String(str ?? "").replace(/\{(\w+)\}/g, (match, key) =>
      key in vars ? this.#escapeHtml(vars[key]) : match,
    );
  }

  // Merges translated title/description text (this.stepsValue[key]) with the
  // structural step metadata (element selector, side/align, navigate target).
  #step(key, meta = {}, vars = {}) {
    const content = this.stepsValue[key] || {};
    const popover = {
      title: this.#fill(content.title, vars),
      description: this.#fill(content.description, vars),
    };
    if (meta.side) popover.side = meta.side;
    if (meta.align) popover.align = meta.align;
    const step = { popover };
    if (meta.element) step.element = meta.element;
    if (meta.navigate) step.navigate = meta.navigate;
    return step;
  }

  #dismiss() {
    fetch(this.dismissUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
        Accept: "application/json",
      },
    }).catch(() => {});
  }

  #buildSteps(live) {
    const vars = {
      name: this.tenantNameValue,
      link: this.hasAdaptersUrlValue ? this.adaptersUrlValue : "",
    };
    const dash = this.dashboardUrlValue;
    switch (this.trackValue) {
      case "sysadmin":
        return this.#sysadminSteps(vars);
      case "owner":
        return this.#ownerSteps(vars, dash, live);
      case "admin":
        return this.#adminSteps(vars, dash);
      case "mod":
        return this.#modSteps(vars, dash);
      default:
        return this.#memberSteps(vars, dash);
    }
  }

  #sysadminSteps(vars) {
    return [
      this.#step("welcome", {}, vars),
      this.#step(
        "adapters",
        { element: "a[href*='/system/admin/adapters']", side: "bottom", align: "start" },
        vars,
      ),
      this.#step(
        "tenants",
        { element: "a[href*='/system/admin/tenants']", side: "bottom", align: "start" },
        vars,
      ),
      this.#step(
        "feature_flags",
        { element: "a[href*='/system/admin/feature_flags']", side: "bottom", align: "start" },
        vars,
      ),
      this.#step(
        "registrations",
        {
          element: "a[href*='/system/admin/tenant_registrations']",
          side: "bottom",
          align: "start",
        },
        vars,
      ),
      this.#step(
        "invite_codes",
        { element: "a[href*='/system/admin/invite_codes']", side: "bottom", align: "start" },
        vars,
      ),
      this.#step("done", {}, vars),
    ];
  }

  #ownerSteps(vars, dash, live) {
    const sessionKey = live ? "session_live" : "session_not_live";
    const sessionMeta = live
      ? { navigate: dash, element: "#panel-session-status", side: "bottom", align: "start" }
      : { navigate: dash, element: "#panel-start-stream", side: "right", align: "center" };

    return [
      this.#step("welcome", {}, vars),
      this.#step(
        "connect_platforms",
        { element: "[data-sidebar-target='adminParent']", side: "right", align: "center" },
        vars,
      ),
      this.#step(
        "roles_commands",
        { element: "[data-sidebar-target='adminParent']", side: "right", align: "center" },
        vars,
      ),
      this.#step(
        "dashboard",
        { navigate: dash, element: "#sc-canvas", side: "top", align: "center" },
        vars,
      ),
      this.#step(
        "connections",
        { navigate: dash, element: "#panel-connections", side: "bottom", align: "start" },
        vars,
      ),
      this.#step(sessionKey, sessionMeta, vars),
      this.#step("done", {}, vars),
    ];
  }

  #modSteps(vars, dash) {
    return [
      this.#step("welcome", {}, vars),
      this.#step(
        "dashboard",
        { navigate: dash, element: "#sc-canvas", side: "top", align: "center" },
        vars,
      ),
      this.#step(
        "mod_actions",
        { navigate: dash, element: "#panel-mod-actions", side: "left", align: "center" },
        vars,
      ),
      this.#step(
        "live_chat",
        { navigate: dash, element: "#panel-live-chat", side: "left", align: "center" },
        vars,
      ),
      this.#step(
        "mod_dashboard",
        { element: "[data-sidebar-target='modParent']", side: "right", align: "center" },
        vars,
      ),
      this.#step("done", {}, vars),
    ];
  }

  #adminSteps(vars, dash) {
    return [
      this.#step("welcome", {}, vars),
      this.#step(
        "dashboard",
        { navigate: dash, element: "#sc-canvas", side: "top", align: "center" },
        vars,
      ),
      this.#step(
        "admin_section",
        { element: "[data-sidebar-target='adminParent']", side: "right", align: "center" },
        vars,
      ),
      this.#step("done", {}, vars),
    ];
  }

  #memberSteps(vars, dash) {
    return [
      this.#step("welcome", {}, vars),
      this.#step(
        "dashboard",
        { navigate: dash, element: "#sc-canvas", side: "top", align: "center" },
        vars,
      ),
      this.#step("done", {}, vars),
    ];
  }
}
